import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
// @mui
import {
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormControlLabel,
    Typography,
    Stack,
    CircularProgress,
    Divider,
} from '@mui/material';
// components
import Iconify from 'src/components/iconify';
import axiosInstance, { endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------

export default function PlanStructureTree() {
    const { watch, setValue } = useFormContext();

    // Watch the 3 whitelist fields
    const allowedModules = watch('allowed_modules') || [];
    const allowedMenus = watch('allowed_menus') || [];
    const allowedSubmenus = watch('allowed_submenus') || [];

    const [expandedModules, setExpandedModules] = useState({});
    const [structure, setStructure] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch full structure
    const fetchStructure = useCallback(async () => {
        try {
            setLoading(true);
            const [modulesRes, menusRes, submenusRes] = await Promise.all([
                axiosInstance.get(`${endpoints.moduleMaster.list}?status=active&limit=0`),
                axiosInstance.get(`${endpoints.menuMaster.list}?status=active&limit=0`),
                axiosInstance.get(`${endpoints.submenuMaster.list}?status=active&limit=0`),
            ]);

            const modules = modulesRes.data.data || [];
            const menus = menusRes.data.data || [];
            const submenus = submenusRes.data.data || [];

            // Build hierarchical structure
            const structured = modules.map((module) => ({
                ...module,
                menus: menus
                    .filter((menu) => menu.module?._id === module._id || menu.module === module._id)
                    .map((menu) => ({
                        ...menu,
                        submenus: submenus.filter(
                            (submenu) => submenu.menu?._id === menu._id || submenu.menu === menu._id
                        ),
                    })),
            }));

            setStructure(structured);
        } catch (error) {
            console.error('Failed to fetch structure for plan:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStructure();
    }, [fetchStructure]);

    // Auto-expand modules that contain pre-selected modules, menus, or submenus
    useEffect(() => {
        if (structure.length > 0) {
            const initialExpanded = {};
            structure.forEach((mod) => {
                const hasSelectedModule = (allowedModules || []).includes(mod.module_alias);
                const hasSelectedMenu = mod.menus?.some((m) => (allowedMenus || []).includes(m.menu_alias));
                const hasSelectedSubmenu = mod.menus?.some((m) =>
                    m.submenus?.some((s) => (allowedSubmenus || []).includes(s.submenu_alias))
                );
                if (hasSelectedModule || hasSelectedMenu || hasSelectedSubmenu) {
                    initialExpanded[mod._id] = true;
                }
            });
            if (Object.keys(initialExpanded).length > 0) {
                setExpandedModules((prev) => ({ ...initialExpanded, ...prev }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [structure]);

    // Handle Module Toggle
    const handleModuleToggle = (moduleAlias) => {
        const isSelected = allowedModules.includes(moduleAlias);
        let newModules = [...allowedModules];
        let newMenus = [...allowedMenus];
        let newSubmenus = [...allowedSubmenus];
        const moduleData = structure.find((m) => m.module_alias === moduleAlias);

        if (isSelected) {
            // UNCHECKING: Remove module and all its children
            newModules = newModules.filter((m) => m !== moduleAlias);

            if (moduleData) {
                const childMenuAliases = moduleData.menus.map((m) => m.menu_alias);
                newMenus = newMenus.filter((m) => !childMenuAliases.includes(m));

                const childSubmenuAliases = moduleData.menus.flatMap((m) =>
                    m.submenus.map((s) => s.submenu_alias)
                );
                newSubmenus = newSubmenus.filter((s) => !childSubmenuAliases.includes(s));
            }
        } else {
            // CHECKING: Add the module AND auto-expand its accordion
            newModules.push(moduleAlias);
            if (moduleData) {
                setExpandedModules((prev) => ({
                    ...prev,
                    [moduleData._id]: true,
                }));
            }
        }

        setValue('allowed_modules', newModules, { shouldDirty: true });
        setValue('allowed_menus', newMenus, { shouldDirty: true });
        setValue('allowed_submenus', newSubmenus, { shouldDirty: true });
    };

    // Handle Menu Toggle
    const handleMenuToggle = (moduleAlias, menuAlias) => {
        const isSelected = allowedMenus.includes(menuAlias);
        const newModules = [...allowedModules];
        let newMenus = [...allowedMenus];
        let newSubmenus = [...allowedSubmenus];
        const moduleData = structure.find((m) => m.module_alias === moduleAlias);

        if (isSelected) {
            // UNCHECKING: Remove menu and all its submenus
            newMenus = newMenus.filter((m) => m !== menuAlias);

            const menuData = moduleData?.menus.find((m) => m.menu_alias === menuAlias);
            if (menuData) {
                const childSubmenuAliases = menuData.submenus.map((s) => s.submenu_alias);
                newSubmenus = newSubmenus.filter((s) => !childSubmenuAliases.includes(s));
            }
        } else {
            // CHECKING: Add menu, ensure parent module is checked, AND auto-expand parent accordion
            newMenus.push(menuAlias);
            if (!newModules.includes(moduleAlias)) {
                newModules.push(moduleAlias);
            }
            if (moduleData) {
                setExpandedModules((prev) => ({
                    ...prev,
                    [moduleData._id]: true,
                }));
            }
        }

        setValue('allowed_modules', newModules, { shouldDirty: true });
        setValue('allowed_menus', newMenus, { shouldDirty: true });
        setValue('allowed_submenus', newSubmenus, { shouldDirty: true });
    };

    // Handle Submenu Toggle
    const handleSubmenuToggle = (moduleAlias, menuAlias, submenuAlias) => {
        const isSelected = allowedSubmenus.includes(submenuAlias);
        const newModules = [...allowedModules];
        const newMenus = [...allowedMenus];
        let newSubmenus = [...allowedSubmenus];
        const moduleData = structure.find((m) => m.module_alias === moduleAlias);

        if (isSelected) {
            // UNCHECKING: Just remove the submenu
            newSubmenus = newSubmenus.filter((s) => s !== submenuAlias);
        } else {
            // CHECKING: Add submenu, ensure parent menu/module are checked, AND auto-expand parent accordion
            newSubmenus.push(submenuAlias);
            if (!newMenus.includes(menuAlias)) {
                newMenus.push(menuAlias);
            }
            if (!newModules.includes(moduleAlias)) {
                newModules.push(moduleAlias);
            }
            if (moduleData) {
                setExpandedModules((prev) => ({
                    ...prev,
                    [moduleData._id]: true,
                }));
            }
        }

        setValue('allowed_modules', newModules, { shouldDirty: true });
        setValue('allowed_menus', newMenus, { shouldDirty: true });
        setValue('allowed_submenus', newSubmenus, { shouldDirty: true });
    };

    const handleAccordionChange = (moduleId) => (event, isExpanded) => {
        setExpandedModules((prev) => ({
            ...prev,
            [moduleId]: isExpanded,
        }));
    };

    if (loading) {
        return (
            <Stack alignItems="center" sx={{ py: 3 }}>
                <CircularProgress size={24} />
            </Stack>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Select modules, menus, and submenus allowed in this plan:
            </Typography>

            {structure.map((module) => {
                const moduleAlias = module.module_alias;
                const isModuleSelected = allowedModules.includes(moduleAlias);

                return (
                    <Accordion
                        key={module._id}
                        expanded={expandedModules[module._id] || false}
                        onChange={handleAccordionChange(module._id)}
                        sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                        <AccordionSummary
                            expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                            sx={{ bgcolor: isModuleSelected ? 'primary.lighter' : 'background.neutral' }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isModuleSelected}
                                    />
                                }
                                label={
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {module.name} ({module.module_alias})
                                    </Typography>
                                }
                                onChange={() => handleModuleToggle(moduleAlias)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                            />
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0 }}>
                            {module.menus.map((menu, index) => {
                                const menuAlias = menu.menu_alias;
                                const isMenuSelected = allowedMenus.includes(menuAlias);

                                return (
                                    <Box key={menu._id}>
                                        {index > 0 && <Divider />}
                                        <Box sx={{ px: 3, py: 1.5 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={isMenuSelected}
                                                    />
                                                }
                                                label={
                                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 0.5 }}>
                                                        <Iconify icon={menu.icon || 'solar:menu-dots-bold'} width={18} sx={{ color: 'text.secondary' }} />
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {menu.name} ({menu.menu_alias})
                                                        </Typography>
                                                    </Stack>
                                                }
                                                onChange={() => handleMenuToggle(moduleAlias, menuAlias)}
                                                sx={{ m: 0, cursor: 'pointer', userSelect: 'none' }}
                                            />

                                            {/* Submenus */}
                                            {menu.submenus.length > 0 && (
                                                <Box sx={{ ml: 4, mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {menu.submenus.map((submenu) => {
                                                        const submenuAlias = submenu.submenu_alias;
                                                        const isSubmenuSelected = allowedSubmenus.includes(submenuAlias);

                                                        return (
                                                            <FormControlLabel
                                                                key={submenu._id}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={isSubmenuSelected}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                        {submenu.name} ({submenu.submenu_alias})
                                                                    </Typography>
                                                                }
                                                                onChange={() => handleSubmenuToggle(moduleAlias, menuAlias, submenuAlias)}
                                                                sx={{ m: 0, mr: 2, cursor: 'pointer', userSelect: 'none' }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}
